// public/js/features/files/asset-files-section.js
// The two file surfaces on the asset detail page (docs/asset-files.md §5):
//   • Files  — documents grouped by doc type, list ⇄ thumbnail grid
//   • Photos — a gallery with exactly one "front image"
//
// WHY every path is swallowed: both sections are rendered fire-and-forget from
// the asset detail page, side by side with trips/charts. A file-store hiccup
// must degrade to a small message inside its own card, never take the asset
// page down with it — so nothing here is allowed to reject into the caller.
//
// This module is also the home of the small presentation helpers the explorer
// reuses (icons, byte/date formatting, the AI chip, the lazy-thumbnail mount,
// the rename dialog, the item-action delegation). Keeping ONE implementation
// means a row in the explorer and a row on the asset page can never drift.
import {
  listAssetFiles, getFileUrl, patchFile, deleteFile, listDocTypes, searchFiles,
} from './api.js';
import { openUploader } from './uploader.js';
import { openFileViewer } from './viewer-modal.js';
import { injectFilesCss } from './explorer.css.js';
import { escapeHtml } from '../../utils/escape.js';
import { can, getCurrentUser } from '../../modules/rbac.js';
import { showConfirm } from '../../modules/ui/confirm.js';
import { scopeTenantParam } from '../../modules/tenant-scope.js';
import { getCached as getCachedAssets } from '../assets/asset-store.js';

const VIEW_KEY_FILES = 'fx.assetFilesView';   // 'list' | 'tiles'

// ─────────────────────────────────────────────────────────────────────────────
// Shared presentation helpers (also imported by explorer-page.js)
// ─────────────────────────────────────────────────────────────────────────────

export const isImageType = (ct) => /^image\//i.test(String(ct || ''));

/** FontAwesome class for a file, chosen from the MIME type with a name fallback. */
export function fileIconClass(contentType, fileName = '') {
  const ct = String(contentType || '').toLowerCase();
  const ext = String(fileName || '').split('.').pop().toLowerCase();
  if (ct.startsWith('image/')) return 'fa-solid fa-file-image';
  if (ct.includes('pdf') || ext === 'pdf') return 'fa-solid fa-file-pdf';
  if (ct.includes('word') || ['doc', 'docx'].includes(ext)) return 'fa-solid fa-file-word';
  if (ct.includes('sheet') || ct.includes('excel') || ct.includes('csv') || ['xls', 'xlsx', 'csv'].includes(ext)) return 'fa-solid fa-file-excel';
  if (ct.includes('presentation') || ct.includes('powerpoint') || ['ppt', 'pptx'].includes(ext)) return 'fa-solid fa-file-powerpoint';
  if (ct.startsWith('text/') || ['txt', 'md', 'log'].includes(ext)) return 'fa-solid fa-file-lines';
  if (ct.includes('zip') || ['zip', '7z', 'rar'].includes(ext)) return 'fa-solid fa-file-zipper';
  return 'fa-solid fa-file';
}

/** Human byte size. Returns an em dash for unknown/zero sizes. */
export function formatBytes(n) {
  const b = Number(n);
  if (!Number.isFinite(b) || b <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0, v = b;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

export function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(navigator.language, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(iso); }
}

/** Effective label for a doc type id, falling back to the raw id. */
export function docTypeLabel(docTypes, docTypeId) {
  if (!docTypeId) return 'Other';
  return (docTypes || []).find(t => t.docTypeId === docTypeId)?.label || docTypeId;
}

/** Small chip conveying the AI indexing state; empty when nothing is known. */
export function aiChipHtml(file) {
  const st = file?.ai?.status;
  if (st === 'pending') return '<span class="badge badge-soft badge-soft-amber" title="The AI index is still being built">indexing…</span>';
  if (st === 'indexed') {
    const tip = file.ai?.summary ? escapeHtml(String(file.ai.summary).slice(0, 180)) : 'Searchable by content';
    return `<span class="badge badge-soft badge-soft-green" title="${tip}">AI indexed</span>`;
  }
  if (st === 'failed') return '<span class="badge badge-soft badge-soft-gray" title="Indexing failed — the file is still searchable by name">not indexed</span>';
  return '';
}

/** Does this record have anything worth showing as a picture? */
export function hasThumb(file) {
  return !!(file?.thumbKey || isImageType(file?.contentType));
}

// An asset's documents/photos are a handful of rows, so those load straight
// away; only a big explorer grid needs rationing. This many are fetched up
// front, the rest wait for the observer.
const EAGER_THUMBS = 24;

/**
 * Resolve thumbnail URLs. Placeholders carry `data-thumb-id` (+ optional
 * `data-thumb-alt`) and become an <img> once their URL is presigned.
 *
 * The first `EAGER_THUMBS` are fetched immediately and the rest lazily, near
 * the viewport. WHY not lazily throughout: an IntersectionObserver delivers
 * nothing while the tab is hidden or render-throttled, so a purely observer-
 * driven list came back from a background tab still showing type icons. The
 * eager head guarantees the common case, the observer still keeps an explorer
 * with 500 files from firing 500 presign requests at once, and `visibilitychange`
 * flushes whatever the observer missed. Returns a disconnect function.
 */
export function mountThumbs(root) {
  const nodes = [...(root?.querySelectorAll('[data-thumb-id]') || [])];
  if (!nodes.length) return () => {};

  const load = async (node) => {
    if (node.dataset.thumbLoaded) return;
    node.dataset.thumbLoaded = '1';
    try {
      // The api layer may hand back the bare URL or the raw envelope.
      const raw = await getFileUrl(node.dataset.thumbId, 'thumb');
      const url = (typeof raw === 'string' ? raw : raw?.url) || null;
      if (!url || !node.isConnected) return;
      const img = new Image();
      img.decoding = 'async';
      img.alt = node.dataset.thumbAlt || '';
      // Swap in only once decoded, so a failed thumbnail keeps the type icon.
      img.addEventListener('load', () => { if (node.isConnected) node.replaceChildren(img); }, { once: true });
      img.src = url;
    } catch (e) { /* keep the icon fallback */ }
  };

  const eager = nodes.slice(0, EAGER_THUMBS);
  const lazy = nodes.slice(EAGER_THUMBS);
  eager.forEach(load);

  if (!lazy.length || typeof IntersectionObserver === 'undefined') {
    lazy.forEach(load);
    return () => {};
  }

  const io = new IntersectionObserver((entries) => {
    for (const e of entries) if (e.isIntersecting) { io.unobserve(e.target); load(e.target); }
  }, { rootMargin: '250px' });
  lazy.forEach(n => io.observe(n));

  // Coming back to a backgrounded tab: load anything on screen the observer
  // never reported, then let it resume for the rest.
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    for (const n of lazy) {
      if (n.dataset.thumbLoaded || !n.isConnected) continue;
      const r = n.getBoundingClientRect();
      if (r.bottom > -250 && r.top < (window.innerHeight || 0) + 250) { io.unobserve(n); load(n); }
    }
  };
  document.addEventListener('visibilitychange', onVisible);

  return () => { io.disconnect(); document.removeEventListener('visibilitychange', onVisible); };
}

/** Thumbnail placeholder markup — the icon shows until the real image lands. */
export function thumbHtml(file, size = 'sm') {
  const cls = size === 'lg' ? 'fx-thumb fx-thumb-lg' : 'fx-thumb fx-thumb-sm';
  const icon = `<i class="${fileIconClass(file.contentType, file.fileName)}"></i>`;
  if (!hasThumb(file)) return `<div class="${cls}">${icon}</div>`;
  const alt = escapeHtml(file.ai?.summary || file.label || file.fileName || '');
  return `<div class="${cls}" data-thumb-id="${escapeHtml(file.fileId)}" data-thumb-alt="${alt}">${icon}</div>`;
}

/**
 * Rename / re-file dialog. Resolves to { label, docTypeId } or null on cancel.
 * Photos get no doc-type select (they are not filed by type).
 */
export function openRenameModal(file, docTypes = []) {
  injectFilesCss();
  return new Promise((resolve) => {
    const id = `fx-rename-${Math.random().toString(36).slice(2)}`;
    const isPhoto = file.itemType === 'photo';
    const options = (docTypes || []).filter(t => t.enabled !== false)
      .map(t => `<option value="${escapeHtml(t.docTypeId)}" ${t.docTypeId === file.docTypeId ? 'selected' : ''}>${escapeHtml(t.label || t.docTypeId)}</option>`)
      .join('');

    document.body.insertAdjacentHTML('beforeend', `
      <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header py-2"><h5 class="modal-title">Rename ${isPhoto ? 'photo' : 'document'}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button></div>
            <div class="modal-body">
              <label class="form-label small mb-1" for="${id}-label">Label</label>
              <input id="${id}-label" class="form-control form-control-sm mb-3" maxlength="160"
                     value="${escapeHtml(file.label || '')}" placeholder="${escapeHtml(file.fileName || 'Label')}">
              ${(!isPhoto && options) ? `
                <label class="form-label small mb-1" for="${id}-type">Document type</label>
                <select id="${id}-type" class="form-select form-select-sm">${options}</select>` : ''}
              <div class="form-text mt-2">The label is what everyone sees in lists and search results — the original file name is kept either way.</div>
            </div>
            <div class="modal-footer py-2">
              <button type="button" class="btn btn-sm btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-sm btn-primary" data-role="save">Save</button>
            </div>
          </div>
        </div>
      </div>`);

    const el = document.getElementById(id);
    const modal = new bootstrap.Modal(el);
    let result = null;
    el.querySelector('[data-role="save"]').addEventListener('click', () => {
      result = {
        label: el.querySelector(`#${id}-label`).value.trim(),
        docTypeId: el.querySelector(`#${id}-type`)?.value || file.docTypeId || undefined,
      };
      modal.hide();
    });
    el.addEventListener('hidden.bs.modal', () => { el.remove(); resolve(result); }, { once: true });
    modal.show();
  });
}

/**
 * Best-effort tenant id for an asset: the files themselves know it, otherwise
 * the cached asset row, otherwise the active tenant scope. Needed for the
 * doc-type list, which is per tenant.
 */
export function resolveTenantId(assetUUID, files = []) {
  const fromFile = files.find(f => f.tenantId)?.tenantId;
  if (fromFile) return fromFile;
  try {
    const a = getCachedAssets().find(x => x.assetUUID === assetUUID);
    if (a?.tenantId) return a.tenantId;
  } catch (e) { /* store not initialised on this page — fall through */ }
  const scoped = scopeTenantParam();
  return scoped && scoped !== 'unassigned' ? scoped : null;
}

/**
 * Delegated view/rename/delete wiring for a container holding `[data-file]`
 * items. Attach ONCE per container and keep `ctx` alive across redraws — the
 * handler reads `ctx.files` at click time, so re-rendering the markup never
 * needs (and must never do) a second attach.
 *
 * ctx = { files, docTypes, canManage, canDelete, onChanged, assetNameOf }
 */
export function wireItemActions(root, ctx) {
  root.addEventListener('click', async (ev) => {
    const item = ev.target.closest('[data-file]');
    if (!item || !root.contains(item)) return;
    const file = (ctx.files || []).find(f => f.fileId === item.dataset.file);
    if (!file) return;

    const act = ev.target.closest('[data-act]')?.dataset.act || 'view';
    if (!['view', 'rename', 'delete'].includes(act)) return;   // e.g. photo "primary"
    ev.preventDefault();
    ev.stopPropagation();

    if (act === 'view') {
      openFileViewer(file, { docTypes: ctx.docTypes, assetName: ctx.assetNameOf?.(file) });
      return;
    }

    if (act === 'rename' && ctx.canManage) {
      const next = await openRenameModal(file, ctx.docTypes || []);
      if (!next) return;
      try {
        await patchFile(file.fileId, { label: next.label, docTypeId: next.docTypeId });
        window.showToast?.('File updated', 'success');
        ctx.onChanged?.();
      } catch (e) { window.showToast?.(`Rename failed: ${e.message}`, 'danger'); }
      return;
    }

    if (act === 'delete' && ctx.canDelete) {
      const ok = await showConfirm({
        title: 'Delete file?',
        body: `<p class="mb-0">“${escapeHtml(file.label || file.fileName || 'This file')}” will be removed permanently, including its stored copies.</p>`,
        confirmText: 'Delete',
      });
      if (!ok) return;
      try {
        await deleteFile(file.fileId);
        window.showToast?.('File deleted', 'success');
        ctx.onChanged?.();
      } catch (e) { window.showToast?.(`Delete failed: ${e.message}`, 'danger'); }
    }
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Files section
// ─────────────────────────────────────────────────────────────────────────────

const readFilesView = () => (localStorage.getItem(VIEW_KEY_FILES) === 'tiles' ? 'tiles' : 'list');
const writeFilesView = (v) => { try { localStorage.setItem(VIEW_KEY_FILES, v); } catch (e) {} };

// ── Compact per-asset search ────────────────────────────────────────────────
// The same search the Files page offers, narrowed to ONE asset — including the
// AI mode, which the server scopes to this asset so the answer never comes from
// another machine's documents. Kept to a single input plus one toggle: the
// asset page has no room for the explorer's full filter row.

/** Search-bar markup. `kindLabel` is 'documents' or 'photos'. */
function searchBarHtml(ctx, kindLabel) {
  const busy = ctx.searching ? '<span class="spinner-border spinner-border-sm text-secondary"></span>' : '';
  return `
    <div class="input-group input-group-sm fx-search" style="max-width:320px;">
      <span class="input-group-text bg-body">${busy || '<i class="fa-solid fa-magnifying-glass text-muted"></i>'}</span>
      <input type="search" class="form-control" data-role="q" value="${escapeHtml(ctx.q || '')}"
             placeholder="Search ${kindLabel}, content…" aria-label="Search ${kindLabel}">
      <button type="button" class="btn ${ctx.ask ? 'btn-primary' : 'btn-outline-secondary'}" data-role="ask"
              title="Ask a question in plain language">
        <i class="fa-solid fa-wand-magic-sparkles"></i>
      </button>
      ${ctx.q ? '<button type="button" class="btn btn-outline-secondary" data-role="clear" title="Clear"><i class="fa-solid fa-xmark"></i></button>' : ''}
    </div>`;
}

/** The AI answer, shown above the results when the AI mode returned one. */
function answerHtml(ctx) {
  if (!ctx.answer?.answer) return '';
  return `
    <div class="alert alert-primary py-2 px-3 small mb-2">
      <i class="fa-solid fa-wand-magic-sparkles me-1"></i>${escapeHtml(ctx.answer.answer)}
    </div>`;
}

/**
 * Wire the search bar. `run` re-queries and redraws.
 * The input is re-created on every draw, so focus and caret are restored by
 * hand — without that, typing would drop out of the field after each keystroke.
 */
function wireSearch(container, ctx, run) {
  const input = container.querySelector('[data-role="q"]');
  if (input && ctx.focusSearch) {
    input.focus();
    const n = input.value.length;
    try { input.setSelectionRange(n, n); } catch { /* type=search on some browsers */ }
  }
  input?.addEventListener('input', () => {
    ctx.q = input.value;
    ctx.focusSearch = true;
    clearTimeout(ctx._t);
    ctx._t = setTimeout(run, 350);
  });
  container.querySelector('[data-role="ask"]')?.addEventListener('click', () => {
    ctx.ask = !ctx.ask;
    ctx.focusSearch = false;
    run();
  });
  container.querySelector('[data-role="clear"]')?.addEventListener('click', () => {
    ctx.q = ''; ctx.answer = null; ctx.results = null; ctx.focusSearch = false;
    run();
  });
}

/** Run the query; null results mean "not searching, show everything". */
async function runSearch(ctx, { assetUUID, kind }) {
  const q = (ctx.q || '').trim();
  if (!q) { ctx.results = null; ctx.answer = null; return; }
  const tenantId = resolveTenantId(assetUUID, ctx.files);
  if (!tenantId) { ctx.results = null; return; }
  try {
    const { results, answer } = await searchFiles({ tenantId, q, ask: ctx.ask, assetUUID, kind });
    ctx.results = results || [];
    ctx.answer = answer || null;
  } catch (e) {
    console.warn('[files] search failed', e?.message);
    ctx.results = []; ctx.answer = null;
  }
}

export async function renderAssetFilesSection(container, assetUUID) {
  if (!container) return;
  injectFilesCss();
  container.innerHTML = '<div class="text-muted small">Loading documents…</div>';

  const groups = getCurrentUser()?.groups || [];
  const ctx = {
    files: [],
    docTypes: [],
    canManage: can(groups, 'manageAssetFiles'),
    canDelete: can(groups, 'deleteAssetFiles'),
    onChanged: () => refresh(),
    // Search state. `results: null` means "not searching" — an empty array is a
    // search that legitimately matched nothing, which reads differently.
    q: '', ask: false, results: null, answer: null, searching: false, focusSearch: false,
  };
  let detachThumbs = () => {};

  // Attached once — see wireItemActions().
  wireItemActions(container, ctx);

  async function load() {
    ctx.files = (await listAssetFiles(assetUUID, { kind: 'file' })) || [];
    const tenantId = resolveTenantId(assetUUID, ctx.files);
    if (tenantId) {
      // A missing doc-type list only costs nicer labels — never block the list.
      try { ctx.docTypes = (await listDocTypes(tenantId)) || []; } catch (e) { ctx.docTypes = []; }
    }
  }

  function draw() {
    detachThumbs();
    const view = readFilesView();
    const searching = ctx.results !== null;
    const files = searching ? ctx.results : ctx.files;

    const counter = searching
      ? `${files.length} of ${ctx.files.length} document${ctx.files.length === 1 ? '' : 's'}`
      : `${files.length} document${files.length === 1 ? '' : 's'}`;

    const toolbar = `
      <div class="fx-toolbar mb-2">
        <span class="small text-muted">${counter}</span>
        <div class="ms-auto d-flex align-items-center gap-2 flex-wrap justify-content-end">
          ${ctx.files.length ? searchBarHtml(ctx, 'documents') : ''}
          ${ctx.files.length ? `
            <div class="btn-group btn-group-sm fx-viewtoggle" role="group" aria-label="View mode">
              <button type="button" class="btn ${view === 'list' ? 'btn-primary' : 'btn-outline-secondary'}" data-view="list" title="List"><i class="fa-solid fa-list"></i></button>
              <button type="button" class="btn ${view === 'tiles' ? 'btn-primary' : 'btn-outline-secondary'}" data-view="tiles" title="Thumbnails"><i class="fa-solid fa-table-cells-large"></i></button>
            </div>` : ''}
          ${ctx.canManage ? '<button type="button" class="btn btn-sm btn-primary" data-role="upload"><i class="fa-solid fa-upload me-1"></i>Upload</button>' : ''}
        </div>
      </div>`;

    // A search that matched nothing is a different state from "no documents".
    if (searching && !files.length) {
      container.innerHTML = `${toolbar}${answerHtml(ctx)}
        <div class="fx-empty-sm"><i class="fa-regular fa-face-frown me-1"></i>Nothing matches "${escapeHtml(ctx.q)}" on this asset.</div>`;
      wireChrome();
      return;
    }

    if (!files.length) {
      container.innerHTML = `
        ${toolbar}
        <div class="fx-empty-sm">
          <i class="fa-regular fa-folder-open me-1"></i>
          No documents yet${ctx.canManage ? ' — upload a certificate, manual or inspection report to keep it with the asset.' : '.'}
        </div>`;
      wireChrome();
      return;
    }

    // Group by doc type; the tenant's own type order first, unknown ids last.
    const order = new Map((ctx.docTypes || []).map((t, i) => [t.docTypeId, i]));
    const buckets = new Map();
    for (const f of files) {
      const key = f.docTypeId || 'other';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(f);
    }
    const keys = [...buckets.keys()].sort((a, b) =>
      (order.has(a) ? order.get(a) : 999) - (order.has(b) ? order.get(b) : 999) ||
      String(docTypeLabel(ctx.docTypes, a)).localeCompare(String(docTypeLabel(ctx.docTypes, b))));

    const groupsHtml = keys.map(key => {
      const rows = buckets.get(key).sort((a, b) => String(b.uploadedAt || '').localeCompare(String(a.uploadedAt || '')));
      const body = view === 'tiles'
        ? `<div class="fx-tiles p-2">${rows.map(f => fileTileHtml(f, ctx)).join('')}</div>`
        : rows.map(f => fileRowHtml(f, ctx)).join('');
      return `
        <div class="card shadow-sm mb-2">
          <div class="fx-group-head">
            <i class="fa-solid fa-folder"></i>${escapeHtml(docTypeLabel(ctx.docTypes, key))}
            <span class="badge badge-soft badge-soft-gray ms-1">${rows.length}</span>
          </div>
          ${body}
        </div>`;
    }).join('');

    container.innerHTML = toolbar + answerHtml(ctx) + groupsHtml;
    wireChrome();
    detachThumbs = mountThumbs(container);
  }

  // Buttons that live in freshly rendered markup — safe to (re)bind each draw.
  function wireChrome() {
    container.querySelectorAll('[data-view]').forEach(btn => btn.addEventListener('click', () => {
      writeFilesView(btn.dataset.view);
      draw();
    }));
    container.querySelector('[data-role="upload"]')?.addEventListener('click', () => {
      openUploader({ assetUUID, kind: 'file', docTypes: ctx.docTypes, onDone: () => refresh() });
    });
    wireSearch(container, ctx, async () => {
      ctx.searching = true; draw();
      await runSearch(ctx, { assetUUID, kind: 'file' });
      ctx.searching = false; draw();
    });
  }

  async function refresh() {
    try { await load(); draw(); }
    catch (e) { console.warn('[files] reload failed', e?.message); }
  }

  try {
    await load();
    draw();
  } catch (e) {
    console.warn('[files] asset files section failed', e?.message);
    container.innerHTML = '<div class="text-muted small">Documents are unavailable right now.</div>';
  }
}

// A single document row (list view). Exported for the explorer's list mode.
export function fileRowHtml(f, { canManage, canDelete, extraSub = '' }) {
  return `
    <div class="fx-row" data-file="${escapeHtml(f.fileId)}">
      ${thumbHtml(f, 'sm')}
      <div class="fx-row-main">
        <div class="fx-row-title">${escapeHtml(f.label || f.fileName || 'Untitled')}</div>
        <div class="fx-row-sub">
          ${extraSub}
          <span>${escapeHtml(formatBytes(f.size))}</span>
          <span>${escapeHtml(formatDate(f.uploadedAt))}</span>
          ${aiChipHtml(f)}
        </div>
      </div>
      <div class="fx-row-actions">
        <button type="button" class="btn btn-sm btn-outline-secondary" data-act="view" title="Open"><i class="fa-solid fa-eye"></i></button>
        ${canManage ? '<button type="button" class="btn btn-sm btn-outline-secondary" data-act="rename" title="Rename"><i class="fa-solid fa-pen"></i></button>' : ''}
        ${canDelete ? '<button type="button" class="btn btn-sm btn-outline-danger" data-act="delete" title="Delete"><i class="fa-solid fa-trash"></i></button>' : ''}
      </div>
    </div>`;
}

// A single document tile (thumbnail view). Exported for the explorer's tiles.
export function fileTileHtml(f, { canManage, canDelete, extraSub = '' }) {
  return `
    <div class="fx-tile" data-file="${escapeHtml(f.fileId)}">
      ${thumbHtml(f, 'lg')}
      <div class="fx-tile-body">
        <div class="fx-tile-title" title="${escapeHtml(f.label || f.fileName || '')}">${escapeHtml(f.label || f.fileName || 'Untitled')}</div>
        <div class="fx-tile-meta">${extraSub}<span>${escapeHtml(formatBytes(f.size))}</span><span>${escapeHtml(formatDate(f.uploadedAt))}</span></div>
        ${aiChipHtml(f) ? `<div>${aiChipHtml(f)}</div>` : ''}
        <div class="fx-tile-actions">
          <button type="button" class="btn btn-sm btn-outline-secondary" data-act="view" title="Open"><i class="fa-solid fa-eye"></i></button>
          ${canManage ? '<button type="button" class="btn btn-sm btn-outline-secondary" data-act="rename" title="Rename"><i class="fa-solid fa-pen"></i></button>' : ''}
          ${canDelete ? '<button type="button" class="btn btn-sm btn-outline-danger" data-act="delete" title="Delete"><i class="fa-solid fa-trash"></i></button>' : ''}
        </div>
      </div>
    </div>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Photos section
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {object} [opts]
 * @param {Function} [opts.onChanged] Called after any change that alters the set
 *   of photos or which one is the front image. The asset detail page uses it to
 *   keep the gallery at the top of Basic Information in step — without it the
 *   two surfaces disagree until the next page load.
 */
export async function renderAssetPhotosSection(container, assetUUID, { onChanged: notify } = {}) {
  if (!container) return;
  injectFilesCss();
  container.innerHTML = '<div class="text-muted small">Loading photos…</div>';

  const groups = getCurrentUser()?.groups || [];
  const ctx = {
    files: [],
    docTypes: [],
    canManage: can(groups, 'manageAssetFiles'),
    canDelete: can(groups, 'deleteAssetFiles'),
    onChanged: () => refresh(),
    q: '', ask: false, results: null, answer: null, searching: false, focusSearch: false,
  };
  let detachThumbs = () => {};

  wireItemActions(container, ctx);

  // "Make front image" is photo-only, so it is handled here rather than in the
  // shared action delegate. Attached once, like the shared one.
  container.addEventListener('click', async (ev) => {
    const btn = ev.target.closest('[data-act="primary"]');
    if (!btn || !container.contains(btn)) return;
    const item = btn.closest('[data-file]');
    if (!item) return;
    ev.preventDefault();
    ev.stopPropagation();
    try {
      await patchFile(item.dataset.file, { makePrimary: true });
      window.showToast?.('Front image updated', 'success');
      refresh();
    } catch (e) { window.showToast?.(`Could not set the front image: ${e.message}`, 'danger'); }
  });

  async function load() {
    const photos = (await listAssetFiles(assetUUID, { kind: 'photo' })) || [];
    // Front image first, then newest — the gallery should open on the picture
    // that represents the asset everywhere else in the app.
    photos.sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)
      || String(b.uploadedAt || '').localeCompare(String(a.uploadedAt || '')));
    ctx.files = photos;
  }

  function draw() {
    detachThumbs();
    const searching = ctx.results !== null;
    const photos = searching ? ctx.results : ctx.files;

    const counter = searching
      ? `${photos.length} of ${ctx.files.length} photo${ctx.files.length === 1 ? '' : 's'}`
      : `${photos.length} photo${photos.length === 1 ? '' : 's'}`;

    const toolbar = `
      <div class="fx-toolbar mb-2">
        <span class="small text-muted">${counter}</span>
        <div class="ms-auto d-flex align-items-center gap-2 flex-wrap justify-content-end">
          ${ctx.files.length ? searchBarHtml(ctx, 'photos') : ''}
          ${ctx.canManage ? '<button type="button" class="btn btn-sm btn-primary" data-role="upload"><i class="fa-solid fa-camera me-1"></i>Add photos</button>' : ''}
        </div>
      </div>`;

    // Photos are searchable by what the AI saw in them, so a miss is worth
    // distinguishing from "this asset has no photos".
    if (searching && !photos.length) {
      container.innerHTML = `${toolbar}${answerHtml(ctx)}
        <div class="fx-empty-sm"><i class="fa-regular fa-face-frown me-1"></i>No photo matches "${escapeHtml(ctx.q)}" on this asset.</div>`;
      wireChrome();
      return;
    }

    if (!photos.length) {
      container.innerHTML = `
        ${toolbar}
        <div class="fx-empty-sm">
          <i class="fa-regular fa-image me-1"></i>
          No photos yet${ctx.canManage ? ' — the first one you add becomes the front image shown in lists and on the map.' : '.'}
        </div>`;
      wireChrome();
      return;
    }

    container.innerHTML = `
      ${toolbar}
      ${answerHtml(ctx)}
      <div class="fx-photos">
        ${photos.map(p => `
          <div class="fx-photo ${p.isPrimary ? 'is-primary' : ''}" data-file="${escapeHtml(p.fileId)}"
               title="${escapeHtml(p.label || p.fileName || '')}">
            ${p.isPrimary ? '<span class="fx-photo-badge">Front image</span>' : ''}
            <div class="fx-thumb" style="width:100%;height:100%;border:0;border-radius:0;"
                 data-thumb-id="${escapeHtml(p.fileId)}" data-thumb-alt="${escapeHtml(p.ai?.summary || p.label || '')}">
              <i class="fa-solid fa-image"></i>
            </div>
            <div class="fx-photo-actions">
              <button type="button" class="btn btn-light" data-act="view" title="Open"><i class="fa-solid fa-eye"></i></button>
              ${(ctx.canManage && !p.isPrimary) ? '<button type="button" class="btn btn-light" data-act="primary" title="Make front image"><i class="fa-solid fa-star"></i></button>' : ''}
              ${ctx.canDelete ? '<button type="button" class="btn btn-light text-danger" data-act="delete" title="Delete"><i class="fa-solid fa-trash"></i></button>' : ''}
            </div>
          </div>`).join('')}
      </div>`;

    wireChrome();
    detachThumbs = mountThumbs(container);
  }

  function wireChrome() {
    container.querySelector('[data-role="upload"]')?.addEventListener('click', () => {
      openUploader({ assetUUID, kind: 'photo', docTypes: [], onDone: () => refresh() });
    });
    wireSearch(container, ctx, async () => {
      ctx.searching = true; draw();
      await runSearch(ctx, { assetUUID, kind: 'photo' });
      ctx.searching = false; draw();
    });
  }

  async function refresh() {
    try { await load(); draw(); notify?.(); }
    catch (e) { console.warn('[files] photo reload failed', e?.message); }
  }

  try {
    await load();
    draw();
  } catch (e) {
    console.warn('[files] asset photos section failed', e?.message);
    container.innerHTML = '<div class="text-muted small">Photos are unavailable right now.</div>';
  }
}
